import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { adminAPI, nmiAPI } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
    Settings,
    DollarSign,
    MessageSquare,
    ArrowLeft,
    Save,
    CheckCircle2,
    AlertCircle,
    KeyRound,
    Send,
    Gift,
} from "lucide-react";
import { toast } from "sonner";

const PRICE_FIELDS = [
    { key: "FULL_ACCESS_PRICE", label: "One-time full access ($)", helper: "What consumers pay for lifetime access." },
    { key: "MONTHLY_SUBSCRIPTION_PRICE", label: "Monthly subscription ($)", helper: "Recurring price for the cancel-anytime plan." },
    { key: "MAIL_FEE_PRICE", label: "Single letter mail fee ($)", helper: "What you charge to mail a letter to one bureau via USPS." },
    { key: "MAIL_BATCH_PRICE", label: "All 3 bureaus mail fee ($)", helper: "Batch price when consumers mail to Equifax + Experian + TransUnion at once." },
];

const TWILIO_FIELDS = [
    { key: "TWILIO_ACCOUNT_SID", label: "Account SID", placeholder: "AC...", helper: "From the Twilio console homepage." },
    { key: "TWILIO_AUTH_TOKEN", label: "Auth Token", placeholder: "(hidden)", helper: "Treat like a password.", secret: true },
    { key: "TWILIO_PHONE_NUMBER", label: "Sender phone (E.164)", placeholder: "+15551234567", helper: "Twilio number you bought." },
];

const STRIPE_FIELDS = [
    { key: "STRIPE_API_KEY", label: "Stripe Secret Key", placeholder: "sk_live_... or sk_test_...", helper: "From dashboard.stripe.com → Developers → API keys. Use sk_live_ for real payments, sk_test_ for testing.", secret: true },
];

const NMI_FIELDS = [
    { key: "NMI_PUBLIC_KEY", label: "Public tokenization key", placeholder: "checkout_public_...", helper: "Sent to the browser so Collect.js can tokenize cards. Safe to expose." },
    { key: "NMI_PRIVATE_KEY", label: "Private security key", placeholder: "(hidden)", helper: "NEVER share. Used server-side to submit transactions to NMI.", secret: true },
    { key: "NMI_MONTHLY_PLAN_ID", label: "Monthly plan ID", placeholder: "creditfix_monthly_usd", helper: "The recurring-plan ID at NMI that monthly subscribers are enrolled in. Click 'Create/refresh plan at NMI' to provision it." },
];

const AdminPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [settings, setSettings] = useState({});
    const [pendingChanges, setPendingChanges] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testSmsTo, setTestSmsTo] = useState(user?.phone || "");
    const [twilioBusy, setTwilioBusy] = useState(false);
    const [nmiPlanBusy, setNmiPlanBusy] = useState(false);
    const [nmiTestBusy, setNmiTestBusy] = useState(false);
    const [nmiCollectReady, setNmiCollectReady] = useState(false);
    const [nmiBilling, setNmiBilling] = useState(() => {
        try { return JSON.parse(localStorage.getItem("nmi_test_billing") || "{}"); }
        catch { return {}; }
    });
    const nmiBillingRef = useRef(nmiBilling);
    useEffect(() => { nmiBillingRef.current = nmiBilling; }, [nmiBilling]);
    const updateBilling = (k, v) => {
        setNmiBilling(prev => {
            const next = { ...prev, [k]: v };
            try { localStorage.setItem("nmi_test_billing", JSON.stringify(next)); } catch { /* ignore */ }
            return next;
        });
    };

    const loadSettings = useCallback(async () => {
        setLoading(true);
        try {
            const r = await adminAPI.getSettings();
            setSettings(r.data);
            setPendingChanges({});
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not load settings");
            if (e.response?.status === 403) navigate("/dashboard");
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        if (!user?.is_admin) {
            toast.error("Admin only.");
            navigate("/dashboard");
            return;
        }
        loadSettings();
    }, [user?.is_admin, navigate, loadSettings]);

    const onChange = (key, value) => {
        setPendingChanges((prev) => ({ ...prev, [key]: value }));
    };

    const valueFor = (key) => {
        if (key in pendingChanges) return pendingChanges[key];
        return settings[key]?.value || "";
    };

    const isDirty = Object.keys(pendingChanges).length > 0;

    const handleSave = async () => {
        if (!isDirty) return;
        setSaving(true);
        try {
            await adminAPI.updateSettings(pendingChanges);
            toast.success("Settings saved — taking effect immediately.");
            await loadSettings();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const handleTwilioTest = async () => {
        if (!testSmsTo) {
            toast.error("Enter a phone number to test");
            return;
        }
        setTwilioBusy(true);
        try {
            const r = await adminAPI.testTwilio(testSmsTo);
            if (r.data?.sent) toast.success("Test SMS sent — check your phone");
            else if (r.data?.reason === "twilio_not_configured") toast.message("Twilio creds not set yet — save the SID, token, and number first.");
            else toast.error(`SMS failed: ${r.data?.reason || "unknown"}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Test failed");
        } finally {
            setTwilioBusy(false);
        }
    };

    const handleCreateNmiPlan = async () => {
        setNmiPlanBusy(true);
        try {
            const r = await nmiAPI.createPlan();
            if (r.data?.already_exists) {
                toast.message(`NMI plan '${r.data.plan_id}' already exists at NMI. You're good to go.`);
            } else if (r.data?.success) {
                toast.success(`NMI plan '${r.data.plan_id}' created at $${r.data.amount}/mo.`);
                await loadSettings();
            } else {
                toast.error(r.data?.message || "NMI plan creation failed.");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "NMI plan creation failed.");
        } finally {
            setNmiPlanBusy(false);
        }
    };

    // Load Collect.js on the Admin page so the $1 test-charge button can open
    // the NMI lightbox. Public key comes from /api/nmi/config.
    useEffect(() => {
        let cancelled = false;
        const setupCollect = () => {
            if (!window.CollectJS || cancelled) return;
            try {
                window.CollectJS.configure({
                    variant: "lightbox",
                    styleSniffer: true,
                    country: "US",
                    currency: "USD",
                    price: "1.00",
                    callback: async (tokenResponse) => {
                        if (!tokenResponse?.token) {
                            toast.error("Card tokenization failed.");
                            setNmiTestBusy(false);
                            return;
                        }
                        try {
                            const r = await nmiAPI.testCharge(tokenResponse.token, nmiBillingRef.current);
                            if (r.data?.success) {
                                toast.success(`$1.00 test approved. NMI txn: ${r.data.transaction_id}. Refund it from the NMI portal.`);
                            } else {
                                toast.error(`Test declined: ${r.data?.message || "unknown"}`);
                            }
                        } catch (e) {
                            toast.error(e.response?.data?.detail || "Test-charge request failed");
                        } finally {
                            setNmiTestBusy(false);
                        }
                    },
                    fieldsAvailableCallback: () => { if (!cancelled) setNmiCollectReady(true); },
                });
                setNmiCollectReady(true);
            } catch (e) {
                console.error(e);
            }
        };

        nmiAPI.getConfig().then(({ data }) => {
            if (cancelled || !data?.configured || !data?.public_key) return;
            const url = "https://secure.nmi.com/token/Collect.js";
            const existing = document.querySelector(`script[src="${url}"]`);
            if (existing) {
                if (window.CollectJS) setupCollect();
                else existing.addEventListener("load", setupCollect);
                return;
            }
            const script = document.createElement("script");
            script.src = url;
            script.setAttribute("data-tokenization-key", data.public_key);
            script.async = true;
            script.onload = setupCollect;
            document.head.appendChild(script);
        }).catch(() => {});

        return () => { cancelled = true; };
    }, []);

    const handleNmiTestCharge = () => {
        if (!window.CollectJS || !nmiCollectReady) {
            toast.error("Secure card form still loading. Try again in a second.");
            return;
        }
        if (!nmiBilling.address1 || !nmiBilling.zip) {
            toast.error("Enter at least a billing street address and ZIP — your NMI account requires AVS.");
            return;
        }
        setNmiTestBusy(true);
        try {
            window.CollectJS.startPaymentRequest();
        } catch (e) {
            toast.error("Could not open card entry.");
            setNmiTestBusy(false);
        }
    };

    if (!user?.is_admin) return null;

    return (
        <div className="min-h-screen bg-background">
            <nav className="sticky top-0 z-50 backdrop-blur-md bg-background/80 border-b border-border">
                <div className="max-w-5xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
                    <Link to="/dashboard" className="flex items-center gap-2 text-sm hover:opacity-80 transition-opacity">
                        <ArrowLeft className="h-4 w-4" />
                        Back to dashboard
                    </Link>
                    <div className="flex items-center gap-2">
                        <Settings className="h-4 w-4 text-primary" />
                        <span className="font-medium text-sm">Admin</span>
                    </div>
                </div>
            </nav>

            <main className="max-w-5xl mx-auto px-4 md:px-8 py-10 space-y-8">
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                    <h1 className="font-serif text-3xl">Operator Settings</h1>
                    <p className="text-muted-foreground mt-1 text-sm">
                        Change pricing, plug in Twilio credentials, and verify each integration — all without touching the server.
                    </p>
                </motion.div>

                {loading ? (
                    <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">Loading...</CardContent></Card>
                ) : (
                    <>
                        {/* PRICING */}
                        <Card data-testid="admin-pricing">
                            <CardHeader>
                                <CardTitle className="font-serif flex items-center gap-2">
                                    <DollarSign className="h-5 w-5 text-primary" />
                                    Pricing
                                </CardTitle>
                                <CardDescription>Changes take effect on the next checkout. Existing subscribers keep their original rate.</CardDescription>
                            </CardHeader>
                            <CardContent className="grid md:grid-cols-2 gap-5">
                                {PRICE_FIELDS.map((f) => (
                                    <div key={f.key} className="space-y-1.5">
                                        <Label htmlFor={f.key} className="text-xs font-medium">{f.label}</Label>
                                        <Input
                                            id={f.key}
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={valueFor(f.key)}
                                            onChange={(e) => onChange(f.key, e.target.value)}
                                            data-testid={`admin-${f.key.toLowerCase()}`}
                                        />
                                        <p className="text-[11px] text-muted-foreground">{f.helper}</p>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>

                        {/* TWILIO */}
                        <Card data-testid="admin-twilio">
                            <CardHeader>
                                <CardTitle className="font-serif flex items-center gap-2">
                                    <MessageSquare className="h-5 w-5 text-primary" />
                                    SMS notifications (Twilio)
                                    {settings.TWILIO_ACCOUNT_SID?.is_set && settings.TWILIO_AUTH_TOKEN?.is_set && settings.TWILIO_PHONE_NUMBER?.is_set ? (
                                        <Badge className="bg-emerald-100 text-emerald-700 ml-2">Connected</Badge>
                                    ) : (
                                        <Badge variant="outline" className="ml-2">Not configured</Badge>
                                    )}
                                </CardTitle>
                                <CardDescription>
                                    YOU (the operator) plug these keys in. Your consumers don&apos;t see them — they just receive texts from your Twilio number.
                                    Get them at <a href="https://console.twilio.com" target="_blank" rel="noreferrer" className="underline">console.twilio.com</a>.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {TWILIO_FIELDS.map((f) => {
                                    const isSet = settings[f.key]?.is_set;
                                    return (
                                        <div key={f.key} className="space-y-1.5">
                                            <Label htmlFor={f.key} className="text-xs font-medium flex items-center gap-2">
                                                {f.secret && <KeyRound className="h-3 w-3" />}
                                                {f.label}
                                                {isSet && f.secret && (
                                                    <span className="text-[10px] text-muted-foreground font-mono">{settings[f.key].masked}</span>
                                                )}
                                            </Label>
                                            <Input
                                                id={f.key}
                                                type={f.secret ? "password" : "text"}
                                                value={valueFor(f.key)}
                                                onChange={(e) => onChange(f.key, e.target.value)}
                                                placeholder={f.secret && isSet ? "(stored — paste a new value to replace)" : f.placeholder}
                                                data-testid={`admin-${f.key.toLowerCase()}`}
                                            />
                                            <p className="text-[11px] text-muted-foreground">{f.helper}</p>
                                        </div>
                                    );
                                })}
                                <div className="border-t border-border pt-4 flex flex-wrap gap-3 items-end">
                                    <div className="flex-1 min-w-[200px] space-y-1.5">
                                        <Label htmlFor="twilio-test-to" className="text-xs">Test SMS recipient</Label>
                                        <Input id="twilio-test-to" value={testSmsTo} onChange={(e) => setTestSmsTo(e.target.value)} placeholder="+14155552671" data-testid="admin-twilio-test-to" />
                                    </div>
                                    <Button variant="outline" onClick={handleTwilioTest} disabled={twilioBusy} className="rounded-full" data-testid="admin-twilio-test-btn">
                                        <Send className="h-4 w-4 mr-2" />
                                        {twilioBusy ? "Sending..." : "Send test SMS"}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>

                        {/* STRIPE */}
                        <Card data-testid="admin-stripe">
                            <CardHeader>
                                <CardTitle className="font-serif flex items-center gap-2">
                                    <DollarSign className="h-5 w-5 text-primary" />
                                    Stripe (payments)
                                    {settings.STRIPE_API_KEY?.is_set ? (
                                        <Badge className="bg-emerald-100 text-emerald-700 ml-2">Connected</Badge>
                                    ) : (
                                        <Badge variant="outline" className="ml-2">Not configured</Badge>
                                    )}
                                </CardTitle>
                                <CardDescription>
                                    Paste YOUR Stripe secret key — required for checkout to work in production. Use <code className="font-mono text-[11px] bg-muted px-1 rounded">sk_live_…</code> for real payments or <code className="font-mono text-[11px] bg-muted px-1 rounded">sk_test_…</code> to test. Get it from <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="underline">dashboard.stripe.com → Developers → API keys</a>.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {STRIPE_FIELDS.map((f) => {
                                    const isSet = settings[f.key]?.is_set;
                                    return (
                                        <div key={f.key} className="space-y-1.5">
                                            <Label htmlFor={f.key} className="text-xs font-medium flex items-center gap-2">
                                                <KeyRound className="h-3 w-3" />
                                                {f.label}
                                                {isSet && <span className="text-[10px] text-muted-foreground font-mono">{settings[f.key].masked}</span>}
                                            </Label>
                                            <Input
                                                id={f.key}
                                                type="password"
                                                value={valueFor(f.key)}
                                                onChange={(e) => onChange(f.key, e.target.value)}
                                                placeholder={isSet ? "(stored — paste a new value to replace)" : f.placeholder}
                                                data-testid={`admin-${f.key.toLowerCase()}`}
                                            />
                                            <p className="text-[11px] text-muted-foreground">{f.helper}</p>
                                        </div>
                                    );
                                })}
                                <p className="text-[11px] text-muted-foreground flex items-start gap-1.5 pt-2 border-t border-border">
                                    <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                    Changes take effect immediately — no redeploy needed. If checkout was failing with &quot;Invalid API key provided,&quot; paste your real Stripe key here.
                                </p>
                            </CardContent>
                        </Card>

                        {/* NMI */}
                        <Card data-testid="admin-nmi">
                            <CardHeader>
                                <CardTitle className="font-serif flex items-center gap-2">
                                    <DollarSign className="h-5 w-5 text-primary" />
                                    NMI (payments — currently active)
                                    {settings.NMI_PUBLIC_KEY?.is_set && settings.NMI_PRIVATE_KEY?.is_set ? (
                                        <Badge className="bg-emerald-100 text-emerald-700 ml-2">Connected</Badge>
                                    ) : (
                                        <Badge variant="outline" className="ml-2">Not configured</Badge>
                                    )}
                                </CardTitle>
                                <CardDescription>
                                    NMI is the live payment gateway on checkout. Public key is safe to expose to the browser (used by Collect.js for tokenization); the private security key never leaves the server.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {NMI_FIELDS.map((f) => {
                                    const isSet = settings[f.key]?.is_set;
                                    return (
                                        <div key={f.key} className="space-y-1.5">
                                            <Label htmlFor={f.key} className="text-xs font-medium flex items-center gap-2">
                                                {f.secret && <KeyRound className="h-3 w-3" />}
                                                {f.label}
                                                {isSet && f.secret && (
                                                    <span className="text-[10px] text-muted-foreground font-mono">{settings[f.key].masked}</span>
                                                )}
                                            </Label>
                                            <Input
                                                id={f.key}
                                                type={f.secret ? "password" : "text"}
                                                value={valueFor(f.key)}
                                                onChange={(e) => onChange(f.key, e.target.value)}
                                                placeholder={f.secret && isSet ? "(stored — paste a new value to replace)" : f.placeholder}
                                                data-testid={`admin-${f.key.toLowerCase()}`}
                                            />
                                            <p className="text-[11px] text-muted-foreground">{f.helper}</p>
                                        </div>
                                    );
                                })}
                                <div className="border-t border-border pt-4 space-y-3">
                                    <div>
                                        <Label className="text-xs font-medium">Billing address for $1 test (required by your NMI account&apos;s AVS)</Label>
                                        <p className="text-[11px] text-muted-foreground mb-2">Enter your card&apos;s billing address once — saved locally for next time.</p>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        <Input
                                            placeholder="Street address (e.g. 123 Main St)"
                                            value={nmiBilling.address1 || ""}
                                            onChange={(e) => updateBilling("address1", e.target.value)}
                                            data-testid="admin-nmi-billing-address1"
                                            className="sm:col-span-2"
                                        />
                                        <Input
                                            placeholder="City"
                                            value={nmiBilling.city || ""}
                                            onChange={(e) => updateBilling("city", e.target.value)}
                                            data-testid="admin-nmi-billing-city"
                                        />
                                        <div className="grid grid-cols-2 gap-2">
                                            <Input
                                                placeholder="State (e.g. TX)"
                                                value={nmiBilling.state || ""}
                                                onChange={(e) => updateBilling("state", e.target.value.toUpperCase())}
                                                maxLength={2}
                                                data-testid="admin-nmi-billing-state"
                                            />
                                            <Input
                                                placeholder="ZIP"
                                                value={nmiBilling.zip || ""}
                                                onChange={(e) => updateBilling("zip", e.target.value)}
                                                data-testid="admin-nmi-billing-zip"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-3 items-center pt-1">
                                        <Button
                                            variant="outline"
                                            onClick={handleCreateNmiPlan}
                                            disabled={nmiPlanBusy || !settings.NMI_PRIVATE_KEY?.is_set}
                                            className="rounded-full"
                                            data-testid="admin-nmi-create-plan-btn"
                                        >
                                            {nmiPlanBusy ? "Provisioning…" : "Create / refresh monthly plan at NMI"}
                                        </Button>
                                        <Button
                                            variant="outline"
                                            onClick={handleNmiTestCharge}
                                            disabled={nmiTestBusy || !settings.NMI_PRIVATE_KEY?.is_set}
                                            className="rounded-full border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                                            data-testid="admin-nmi-test-charge-btn"
                                        >
                                            {nmiTestBusy ? "Running $1 test…" : "Run $1 live test charge"}
                                        </Button>
                                    </div>
                                    <p className="text-[11px] text-muted-foreground">
                                        The <strong>$1 test</strong> runs a real sale on NMI to prove the gateway works end-to-end. It does <em>not</em> grant paid access to your account or affect subscribers. Refund it from your NMI portal afterwards.
                                    </p>
                                </div>
                            </CardContent>
                        </Card>

                        {/* REFERRAL PROGRAM */}
                        <Card data-testid="admin-referral">
                            <CardHeader>
                                <CardTitle className="font-serif flex items-center gap-2">
                                    <Gift className="h-5 w-5 text-primary" />
                                    Referral program
                                    {(valueFor("REFERRAL_ENABLED") || "true").toString().toLowerCase() === "true" ? (
                                        <Badge className="bg-emerald-100 text-emerald-700 ml-2">Enabled</Badge>
                                    ) : (
                                        <Badge variant="outline" className="ml-2">Disabled</Badge>
                                    )}
                                </CardTitle>
                                <CardDescription>
                                    Customers who invite a friend earn a percentage off their next paid checkout — and so does the friend. Toggle the whole program on/off or change the discount any time.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="grid md:grid-cols-2 gap-5">
                                <div className="space-y-1.5">
                                    <Label htmlFor="REFERRAL_ENABLED" className="text-xs font-medium">Referral program enabled</Label>
                                    <select
                                        id="REFERRAL_ENABLED"
                                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                                        value={(valueFor("REFERRAL_ENABLED") || "true").toString().toLowerCase() === "true" ? "true" : "false"}
                                        onChange={(e) => onChange("REFERRAL_ENABLED", e.target.value)}
                                        data-testid="admin-referral_enabled"
                                    >
                                        <option value="true">On — discount applies</option>
                                        <option value="false">Off — no discount given</option>
                                    </select>
                                    <p className="text-[11px] text-muted-foreground">Disabling stops new discount accruals immediately. Pending discounts already earned by users remain unaffected until the program is re-enabled.</p>
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="REFERRAL_DISCOUNT_PERCENT" className="text-xs font-medium">Discount percentage (%)</Label>
                                    <Input
                                        id="REFERRAL_DISCOUNT_PERCENT"
                                        type="number"
                                        step="1"
                                        min="0"
                                        max="100"
                                        value={valueFor("REFERRAL_DISCOUNT_PERCENT") || "20"}
                                        onChange={(e) => onChange("REFERRAL_DISCOUNT_PERCENT", e.target.value)}
                                        data-testid="admin-referral_discount_percent"
                                    />
                                    <p className="text-[11px] text-muted-foreground">Applies to one-time, subscription (first invoice), and letter mailing checkouts.</p>
                                </div>
                            </CardContent>
                        </Card>

                        {/* SAVE */}
                        <div className="sticky bottom-4 flex justify-end">
                            <Button
                                onClick={handleSave}
                                disabled={!isDirty || saving}
                                size="lg"
                                className="rounded-full btn-press shadow-lg"
                                data-testid="admin-save-btn"
                            >
                                <Save className="h-4 w-4 mr-2" />
                                {saving ? "Saving..." : isDirty ? `Save ${Object.keys(pendingChanges).length} change${Object.keys(pendingChanges).length === 1 ? "" : "s"}` : "All saved"}
                            </Button>
                        </div>
                    </>
                )}
            </main>
        </div>
    );
};

export default AdminPage;
